import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { IdCard, Trash2, Phone, Mail, Download } from "lucide-react";

const STATUSES = ["pending", "sent", "rejected"];

const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-yellow-400 text-black",
    sent: "bg-green-500 text-black",
    rejected: "bg-red-500 text-white",
  };
  return (
    <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-widest ${colors[status] || "bg-zinc-700 text-white"}`}>
      {status}
    </span>
  );
};

export default function CardRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/card-requests");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/card-requests/${id}?status=${status}`);
      toast.success(`Stato: ${status}`);
      load();
    } catch (err) {
      toast.error("Errore");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/admin/card-requests/${id}`);
      toast.success("Eliminata");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const exportCSV = () => {
    const csv = [
      "first_name,last_name,phone,email,status,created_at",
      ...items.map((s) => `"${s.first_name}","${s.last_name}","${s.phone}","${s.email}","${s.status}","${s.created_at}"`),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daitan-card-requests-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = items.filter((x) => filter === "all" || x.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: items.filter((x) => x.status === s).length }), {});

  return (
    <div data-testid="admin-card-requests" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">忠誠 / DAITA'N'CLUB</div>
          <h1 className="font-heading text-5xl uppercase text-white leading-none">Richieste Card</h1>
          <p className="text-zinc-400 mt-2">
            {items.length} totali · Pending {counts.pending || 0} · Inviate {counts.sent || 0} · Rifiutate {counts.rejected || 0}
          </p>
        </div>
        <button data-testid="export-card-csv" onClick={exportCSV} disabled={!items.length}
          className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition flex items-center gap-2 disabled:opacity-40">
          <Download className="w-5 h-5" strokeWidth={3} /> Esporta CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`card-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 transition ${
              filter === s ? "bg-yellow-400 text-black border-yellow-400" : "border-zinc-700 text-zinc-400 hover:border-yellow-400"
            }`}>
            {s === "all" ? "Tutte" : s}
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-950 border-b-2 border-zinc-800">
            <tr className="text-left">
              {["Cliente", "Contatti", "Data", "Stato", "Azioni"].map((h) => (
                <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-zinc-500 uppercase">Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500 uppercase">Nessuna richiesta</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`card-req-${r.id}`} className="border-b border-zinc-800 hover:bg-zinc-950/50">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-yellow-400 text-black p-2"><IdCard className="w-4 h-4" strokeWidth={3} /></div>
                    <div>
                      <div className="text-white font-bold">{r.first_name} {r.last_name}</div>
                      <div className="text-zinc-500 text-xs font-mono">#{r.id.slice(0, 8).toUpperCase()}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-zinc-300 text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-yellow-400" /> {r.email}</div>
                  <div className="text-zinc-400 text-sm flex items-center gap-1 mt-1"><Phone className="w-3 h-3 text-yellow-400" /> {r.phone}</div>
                </td>
                <td className="p-3 text-zinc-400 text-sm">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <select data-testid={`card-status-${r.id}`} value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="bg-zinc-800 border-2 border-zinc-700 text-white text-xs px-2 py-1 focus:border-yellow-400 focus:outline-none uppercase">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid={`card-delete-${r.id}`} onClick={() => del(r.id)}
                      className="bg-zinc-800 text-red-400 p-2 hover:bg-red-600 hover:text-white transition">
                      <Trash2 className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
